import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChannelType, Permissions as P, type Channel } from '@comms/shared';
import { isUnread, useStore } from '../store';
import { openModal } from '../store/ui';
import { api } from '../lib/api';
import { has, usePermissions } from '../hooks/permissions';
import { Icon, cx } from './ui';
import { VoiceParticipants } from './VoiceUi';
import { joinVoice } from '../lib/voice';

function ChannelRow({
  channel,
  active,
  guildId,
}: {
  channel: Channel;
  active: boolean;
  guildId: string;
}) {
  const unread = useStore((s) => isUnread(s, channel.id));
  const mentions = useStore((s) => s.readStates[channel.id]?.mentionCount ?? 0);
  // Muted itself, or (for a thread) through its channel; a muted server dims the whole list.
  const muted = useStore(
    (s) =>
      !!s.notificationSettings[channel.id]?.muted ||
      (!!channel.threadMetadata && !!s.notificationSettings[channel.parentId ?? '']?.muted),
  );
  const perms = usePermissions(guildId, channel.id);
  const voice = channel.type === ChannelType.GUILD_VOICE;
  const thread = !!channel.threadMetadata;
  const icon = voice
    ? 'speaker'
    : thread
      ? 'thread'
      : channel.type === ChannelType.GUILD_FORUM
        ? 'forum'
        : 'hash';
  return (
    <div
      className={cx(
        'group mx-2 flex items-center rounded px-2',
        thread ? 'ml-7 py-1 text-sm' : 'py-1.5',
        active ? 'bg-bg-active text-text-0' : 'text-text-3 hover:bg-bg-hover hover:text-text-1',
      )}
    >
      <Link
        to={`/channels/${guildId}/${channel.id}`}
        onClick={() => voice && has(perms, P.CONNECT) && void joinVoice(guildId, channel.id)}
        title={voice ? 'Join voice' : undefined}
        className={cx(
          'flex min-w-0 flex-1 items-center gap-1.5',
          unread && !muted && !active && 'font-semibold text-text-0',
          muted && 'opacity-50',
        )}
      >
        <Icon name={icon} className={cx('shrink-0 opacity-70', thread ? 'h-4 w-4' : 'h-5 w-5')} />
        <span className="truncate">{channel.name}</span>
      </Link>
      {mentions > 0 && (
        <span className="ml-1 rounded-full bg-danger px-1.5 text-xs font-bold text-white">
          {mentions}
        </span>
      )}
      {has(perms, P.CREATE_INVITE) && !thread && (
        <button
          type="button"
          title="Invite"
          onClick={() => openModal({ kind: 'invite', channelId: channel.id })}
          className="ml-1 hidden p-1 text-text-3 hover:text-text-0 group-hover:block [@media(hover:none)]:block"
        >
          <Icon name="users" className="h-4 w-4" />
        </button>
      )}
      {has(perms, P.MANAGE_CHANNELS) && !thread && (
        <button
          type="button"
          title="Edit channel"
          onClick={() => openModal({ kind: 'channelSettings', channelId: channel.id })}
          className="ml-1 hidden p-1 text-text-3 hover:text-text-0 group-hover:block [@media(hover:none)]:block"
        >
          <Icon name="settings" className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function GuildSidebar({
  guildId,
  activeChannelId,
}: {
  guildId: string;
  activeChannelId?: string;
}) {
  const guild = useStore((s) => s.guilds[guildId]);
  const channels = useStore((s) => s.channels);
  const meId = useStore((s) => s.me?.id);
  const joinedThreads = useStore((s) => s.joinedThreads);
  const muted = useStore((s) => !!s.notificationSettings[guildId]?.muted);
  const level = useStore((s) => s.notificationSettings[guildId]?.level ?? 'mentions');
  const perms = usePermissions(guildId);
  const [menu, setMenu] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  if (!guild) return <div className="flex-1" />;

  const list = Object.values(channels).filter((c) => c.guildId === guildId);
  const categories = list
    .filter((c) => c.type === ChannelType.GUILD_CATEGORY)
    .sort((a, b) => a.position - b.position);
  const byParent = (parentId: string | null) =>
    list
      .filter(
        (c) =>
          c.type !== ChannelType.GUILD_CATEGORY &&
          !c.threadMetadata &&
          (c.parentId ?? null) === parentId,
      )
      .sort((a, b) => a.position - b.position);
  // Under each channel: active threads I've joined, plus the one I'm looking at.
  const threadsOf = (channelId: string) =>
    list
      .filter(
        (c) =>
          c.parentId === channelId &&
          c.threadMetadata &&
          (c.id === activeChannelId || (joinedThreads[c.id] && !c.threadMetadata.archived)),
      )
      .sort((a, b) =>
        b.threadMetadata!.lastActivityAt.localeCompare(a.threadMetadata!.lastActivityAt),
      );
  const canManage =
    has(perms, P.MANAGE_GUILD) ||
    has(perms, P.MANAGE_ROLES) ||
    has(perms, P.MANAGE_EMOJIS) ||
    has(perms, P.BAN_MEMBERS) ||
    has(perms, P.VIEW_AUDIT_LOG);

  async function leave() {
    if (!confirm(`Leave ${guild!.name}?`)) return;
    await api('DELETE', `/guilds/${guildId}/members/@me`);
    navigate('/channels/@me');
  }
  async function toggleMute() {
    await api('PUT', `/users/@me/notification-settings/${guildId}`, { muted: !muted });
    setMenu(false);
  }

  const items = [
    has(perms, P.CREATE_INVITE) &&
      activeChannelId && {
        label: 'Invite People',
        action: () => openModal({ kind: 'invite', channelId: activeChannelId }),
      },
    canManage && {
      label: 'Server Settings',
      action: () => openModal({ kind: 'guildSettings', guildId }),
    },
    has(perms, P.MANAGE_CHANNELS) && {
      label: 'Create Channel',
      action: () => openModal({ kind: 'createChannel', guildId }),
    },
    has(perms, P.MANAGE_CHANNELS) && {
      label: 'Create Category',
      action: () => openModal({ kind: 'createChannel', guildId, type: 4 }),
    },
    { label: muted ? 'Unmute Server' : 'Mute Server', action: toggleMute },
    ...(['all', 'mentions', 'nothing'] as const).map((l) => ({
      label: `${level === l ? '✓ ' : ''}Notify: ${l === 'all' ? 'All messages' : l === 'mentions' ? 'Only @mentions' : 'Nothing'}`,
      action: () => void api('PUT', `/users/@me/notification-settings/${guildId}`, { level: l }),
    })),
    guild.ownerId !== meId && { label: 'Leave Server', action: leave, danger: true },
  ].filter(Boolean) as { label: string; action: () => void; danger?: boolean }[];

  const renderChannels = (cs: Channel[]) =>
    cs.map((c) => (
      <div key={c.id}>
        <ChannelRow channel={c} guildId={guildId} active={c.id === activeChannelId} />
        {c.type === ChannelType.GUILD_VOICE && (
          <VoiceParticipants channelId={c.id} guildId={guildId} />
        )}
        {threadsOf(c.id).map((t) => (
          <ChannelRow key={t.id} channel={t} guildId={guildId} active={t.id === activeChannelId} />
        ))}
      </div>
    ));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenu(!menu)}
          className="flex h-12 w-full items-center justify-between border-b border-bg-0 px-4 font-semibold text-text-0 hover:bg-bg-hover"
        >
          <span className="truncate">{guild.name}</span>
          <Icon name={menu ? 'x' : 'chevron'} className="h-4 w-4" />
        </button>
        {menu && (
          <div className="absolute left-2 right-2 top-13 z-20 rounded bg-bg-0 p-1.5 shadow-xl">
            {items.map((i) => (
              <button
                key={i.label}
                type="button"
                onClick={() => {
                  setMenu(false);
                  i.action();
                }}
                className={cx(
                  'block w-full rounded px-2 py-1.5 text-left text-sm',
                  i.danger
                    ? 'text-danger hover:bg-danger hover:text-white'
                    : 'text-text-2 hover:bg-accent hover:text-white',
                )}
              >
                {i.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto py-3">
        <button
          type="button"
          onClick={() => openModal({ kind: 'events', guildId })}
          className="mx-2 flex w-[calc(100%-1rem)] items-center gap-1.5 rounded px-2 py-1.5 text-text-3 hover:bg-bg-hover hover:text-text-1"
        >
          <Icon name="calendar" className="h-5 w-5 opacity-70" />
          <span className="flex-1 text-left">
            {guild.events.length
              ? `${guild.events.length} ${guild.events.length === 1 ? 'Event' : 'Events'}`
              : 'Events'}
          </span>
          {guild.events.some((e) => e.status === 2) && (
            <span className="rounded bg-success px-1.5 text-xs font-bold text-white">LIVE</span>
          )}
        </button>
        {renderChannels(byParent(null))}
        {categories.map((cat) => (
          <div key={cat.id} className="pt-4">
            <div className="group flex items-center px-2">
              <button
                type="button"
                onClick={() => setCollapsed({ ...collapsed, [cat.id]: !collapsed[cat.id] })}
                className="flex flex-1 items-center gap-0.5 text-xs font-bold uppercase tracking-wide text-text-3 hover:text-text-1"
              >
                <Icon
                  name="chevron"
                  className={cx('h-3 w-3 transition-transform', collapsed[cat.id] && '-rotate-90')}
                />
                {cat.name}
              </button>
              {has(perms, P.MANAGE_CHANNELS) && (
                <button
                  type="button"
                  title="Create channel"
                  onClick={() => openModal({ kind: 'createChannel', guildId, parentId: cat.id })}
                  className="text-text-3 hover:text-text-0"
                >
                  <Icon name="plus" className="h-4 w-4" />
                </button>
              )}
            </div>
            {!collapsed[cat.id] && renderChannels(byParent(cat.id))}
          </div>
        ))}
      </div>
    </div>
  );
}
